import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { MatchFilters } from '../../utils/MatchService';
import RecipeMatchFilterDialog from './RecipeMatchFilterDialog';

type Props = {
  currentUsername: string;
  partnerName: string;
  readyUsernames: string[];
  waitingForPartner: boolean;
  filters: MatchFilters;
  recipeCount: number;
  onFiltersChange: (filters: MatchFilters) => void;
  onStart: () => void;
  onCancelWaiting: () => void;
};

const Avatar = ({ initial, isReady }: { initial: string; isReady: boolean }) => (
  <Box sx={{ position: 'relative' }}>
    <Box sx={{
      width: 62, height: 62, borderRadius: '50%',
      border: '2px solid', borderColor: 'text.primary',
      bgcolor: 'background.paper',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '1.4rem', fontWeight: 600, color: 'text.secondary',
    }}>
      {initial}
    </Box>
    {isReady && (
      <Box sx={{
        position: 'absolute', bottom: -2, right: -2,
        width: 20, height: 20, borderRadius: '50%',
        bgcolor: 'primary.main', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid white',
      }}>
        <CheckIcon sx={{ fontSize: 12 }} />
      </Box>
    )}
  </Box>
);

const RecipeMatchEntry = ({
  currentUsername, partnerName, readyUsernames, waitingForPartner,
  filters, recipeCount, onFiltersChange, onStart, onCancelWaiting,
}: Props) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const currentUserReady = readyUsernames.includes(currentUsername) || waitingForPartner;
  const partnerReady = readyUsernames.includes(partnerName);
  const filtersLocked = waitingForPartner;

  const removeTag = (tag: string) =>
    onFiltersChange({ ...filters, tags: filters.tags.filter(t => t !== tag) });
  const clearCookTime = () => onFiltersChange({ ...filters, maxCookTime: null });
  const clearDifficulty = () => onFiltersChange({ ...filters, difficulty: null });

  const hasActiveFilters = filters.tags.length > 0 || filters.maxCookTime !== null || filters.difficulty !== null;

  const statusText = currentUserReady && partnerReady
    ? t('match_bothReady', { name: partnerName })
    : partnerReady
      ? t('match_partnerReady', { name: partnerName })
      : currentUserReady
        ? t('match_waitingFor', { name: partnerName })
        : t('match_youAnd', { name: partnerName });

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', flex: 1, gap: 3, px: 3, py: 4,
    }}>
      <Typography variant="h4" fontWeight={700} textAlign="center" sx={{ lineHeight: 1.2 }}>
        {t('match_title')}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar initial={currentUsername.charAt(0).toUpperCase()} isReady={currentUserReady} />
        <Box sx={{ ml: -1.5 }}>
          <Avatar initial={partnerName.charAt(0).toUpperCase()} isReady={partnerReady} />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
        {statusText}
      </Typography>

      {/* Active filter chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', minHeight: 34 }}>
        {filters.tags.map(tag => (
          <Chip
            key={tag}
            label={tag}
            onDelete={filtersLocked ? undefined : () => removeTag(tag)}
            variant="outlined"
            disabled={filtersLocked}
            sx={{ borderWidth: 1.5, borderColor: 'text.primary', bgcolor: '#fbe9d6', color: 'text.primary' }}
          />
        ))}
        {filters.maxCookTime !== null && (
          <Chip
            label={`< ${filters.maxCookTime} min`}
            onDelete={filtersLocked ? undefined : clearCookTime}
            variant="outlined"
            disabled={filtersLocked}
            sx={{ borderWidth: 1.5, borderColor: 'text.primary', bgcolor: '#fbe9d6', color: 'text.primary' }}
          />
        )}
        {filters.difficulty !== null && (
          <Chip
            label={t(filters.difficulty)}
            onDelete={filtersLocked ? undefined : clearDifficulty}
            variant="outlined"
            disabled={filtersLocked}
            sx={{ borderWidth: 1.5, borderColor: 'text.primary', bgcolor: '#fbe9d6', color: 'text.primary' }}
          />
        )}
        {!filtersLocked && (
          <Chip
            label={t('match_addFilter')}
            icon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={() => setDialogOpen(true)}
            variant="outlined"
            sx={{ borderStyle: 'dashed', borderColor: 'text.secondary', color: 'text.secondary' }}
          />
        )}
        {!hasActiveFilters && filtersLocked && (
          <Typography variant="caption" color="text.disabled">{t('match_noFilters')}</Typography>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t('match_recipeCount', { count: recipeCount })}
      </Typography>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={onStart}
        disabled={waitingForPartner || recipeCount === 0}
        startIcon={waitingForPartner ? <CircularProgress size={18} color="inherit" /> : undefined}
        sx={{ borderRadius: 8, py: 1.5, fontSize: '1.05rem', fontWeight: 700, maxWidth: 320 }}
      >
        {waitingForPartner ? t('match_waitingFor', { name: partnerName }) : t('match_startSwiping')}
      </Button>

      {waitingForPartner && (
        <Button
          variant="text"
          size="small"
          onClick={onCancelWaiting}
          sx={{ mt: -2, color: 'text.secondary', fontWeight: 600 }}
        >
          {t('match_cancelWaiting')}
        </Button>
      )}

      <RecipeMatchFilterDialog
        open={dialogOpen}
        initialFilters={filters}
        onClose={(newFilters) => {
          setDialogOpen(false);
          onFiltersChange(newFilters);
        }}
      />
    </Box>
  );
};

export default RecipeMatchEntry;
